import React from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Tooltip,
  Paper
} from '@mui/material';
import {
  IoDocument,
  IoImage,
  IoClose,
  IoEye
} from 'react-icons/io5';
import { adminColors, radius, TOUCH_HIT_SX } from '../../theme/adminTheme';

const StagedContent = ({ stagedFiles, onRemoveFile, onPreviewFile }) => {
  if (!stagedFiles || stagedFiles.length === 0) return null;

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) {
      return <IoImage size={16} />;
    }
    return <IoDocument size={16} />;
  };

  const getFileTypeDisplay = (fileName, fileType) => {
    if (fileType.startsWith('image/')) {
      return 'Image';
    }
    const extension = fileName.split('.').pop()?.toUpperCase();
    return extension || 'File';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="body2"
        sx={{
          color: adminColors.ink,
          fontSize: '13px',
          fontWeight: 600,
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        📎 Attached Files ({stagedFiles.length})
      </Typography>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxHeight: '200px',
        overflowY: 'auto',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(24,24,27,0.06)',
          borderRadius: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(24,24,27,0.2)',
          borderRadius: '2px',
        },
      }}>
        {stagedFiles.map((file) => (
          <Paper
            key={file.id}
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              borderRadius: radius.surface,
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              transition: 'border-color 0.15s ease, background-color 0.15s ease',
              '&:hover': {
                backgroundColor: adminColors.accentLight,
                borderColor: adminColors.accentBorder,
              }
            }}
          >
            {/* File Preview/Icon */}
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: radius.control,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: file.type.startsWith('image/')
                  ? 'transparent'
                  : '#e5e7eb',
                overflow: 'hidden',
                flexShrink: 0
              }}
            >
              {file.type.startsWith('image/') && file.file ? (
                <img
                  src={URL.createObjectURL(file.file)}
                  alt={file.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '8px'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : (
                <Box sx={{ color: adminColors.inkSoft, display: 'flex' }}>
                  {getFileIcon(file.type)}
                </Box>
              )}
            </Box>

            {/* File Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: adminColors.ink,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '14px'
                }}
              >
                {file.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: adminColors.inkSoft,
                  fontSize: '12px',
                  display: 'block'
                }}
              >
                {getFileTypeDisplay(file.name, file.type)} • {formatFileSize(file.size)}
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {file.type.startsWith('image/') && file.file && (
                <Tooltip title="Preview Image">
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (onPreviewFile) {
                        onPreviewFile(file);
                      } else {
                        // Fallback: open in new tab
                        const url = URL.createObjectURL(file.file);
                        window.open(url, '_blank');
                      }
                    }}
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: adminColors.accentLight,
                      color: adminColors.accent,
                      '&:hover': {
                        backgroundColor: adminColors.accentBorder,
                      },
                      transition: 'background-color 0.15s ease',
                      ...TOUCH_HIT_SX
                    }}
                  >
                    <IoEye size={16} />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Remove File">
                <IconButton
                  size="small"
                  onClick={() => onRemoveFile(file.id)}
                  aria-label={`Remove ${file.name}`}
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: adminColors.errorLight,
                    color: adminColors.error,
                    '&:hover': {
                      backgroundColor: adminColors.errorBorder,
                    },
                    transition: 'background-color 0.15s ease',
                    ...TOUCH_HIT_SX
                  }}
                >
                  <IoClose size={16} />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};
export default StagedContent;
